import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import {
  AlertTriangle,
  ArrowLeft,
  CheckCircle2,
  KeyRound,
  Link2,
  Loader2,
  PlugZap,
  ShieldCheck,
  Store,
  XCircle,
} from 'lucide-react';
import { PageContainer } from '../components/layout/PageContainer';
import {
  ConnectedApp,
  PartnerLinkRequest,
  PartnerScope,
  SCOPE_LABELS,
  normalizeCode,
  parseDiscount,
  partnerLinksApi,
} from '../services/partnerLinksApi';

const formatDate = (value: string | null) =>
  value
    ? new Date(value).toLocaleString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })
    : '';

const messageOf = (e: unknown) => (e instanceof Error && e.message ? e.message : 'Une erreur est survenue. Réessayez.');

/**
 * Applications connectées : le propriétaire (ou un admin) autorise une
 * application partenaire, comme Tindisa, à lire son catalogue et à enregistrer
 * des ventes, puis peut couper cet accès à tout moment.
 */
export default function ConnectedAppsPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [code, setCode] = useState(() => normalizeCode(searchParams.get('code') || ''));
  const [request, setRequest] = useState<PartnerLinkRequest | null>(null);
  const [scopes, setScopes] = useState<PartnerScope[]>([]);
  const [businessUnitId, setBusinessUnitId] = useState('');
  const [displayCurrency, setDisplayCurrency] = useState('CDF');
  const [discount, setDiscount] = useState('0');
  const [editing, setEditing] = useState<{ id: string; value: string } | null>(null);
  const [saving, setSaving] = useState(false);
  const [apps, setApps] = useState<ConnectedApp[]>([]);
  const [loadingApps, setLoadingApps] = useState(true);
  const [busy, setBusy] = useState<'preview' | 'approve' | 'refuse' | null>(null);
  const [revoking, setRevoking] = useState<string | null>(null);
  const [confirmRevoke, setConfirmRevoke] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const loadApps = useCallback(async () => {
    setLoadingApps(true);
    try {
      setApps(await partnerLinksApi.list());
    } catch (e) {
      setError(messageOf(e));
    } finally {
      setLoadingApps(false);
    }
  }, []);

  const examine = useCallback(async (raw: string) => {
    const value = normalizeCode(raw);
    setError(null);
    setNotice(null);
    if (value.length !== 8) {
      setError('Le code compte 8 caractères.');
      return;
    }
    setBusy('preview');
    try {
      const found = await partnerLinksApi.preview(value);
      setRequest(found);
      setScopes(found.requestedScopes);
      setBusinessUnitId('');
      setDisplayCurrency(found.displayCurrencies?.[0] || 'CDF');
      setDiscount('0');
    } catch (e) {
      setRequest(null);
      setError(messageOf(e));
    } finally {
      setBusy(null);
    }
  }, []);

  useEffect(() => {
    loadApps();
  }, [loadApps]);

  // Arrivée par le lien envoyé par l'application partenaire (?code=...).
  useEffect(() => {
    const fromUrl = normalizeCode(searchParams.get('code') || '');
    if (fromUrl.length === 8) examine(fromUrl);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const closeRequest = () => {
    setRequest(null);
    setCode('');
    if (searchParams.has('code')) {
      searchParams.delete('code');
      setSearchParams(searchParams, { replace: true });
    }
  };

  const approve = async () => {
    if (!request) return;
    if (!scopes.length) {
      setError('Choisissez au moins une autorisation.');
      return;
    }
    const maxDiscountPct = parseDiscount(discount);
    if (maxDiscountPct === null) {
      setError('La remise maximale doit être comprise entre 0 et 90 %.');
      return;
    }
    setBusy('approve');
    setError(null);
    try {
      await partnerLinksApi.approve({
        code,
        scopes,
        displayCurrency,
        maxDiscountPct,
        ...(businessUnitId ? { businessUnitId } : {}),
      });
      setNotice(`${request.clientName} est maintenant connecté à votre entreprise.`);
      closeRequest();
      await loadApps();
    } catch (e) {
      setError(messageOf(e));
    } finally {
      setBusy(null);
    }
  };

  const refuse = async () => {
    if (!request) return;
    setBusy('refuse');
    setError(null);
    try {
      await partnerLinksApi.refuse(code);
      setNotice('Demande refusée. Aucune donnée n’a été partagée.');
      closeRequest();
    } catch (e) {
      setError(messageOf(e));
    } finally {
      setBusy(null);
    }
  };

  const revoke = async (id: string) => {
    setRevoking(id);
    setError(null);
    try {
      await partnerLinksApi.revoke(id);
      setNotice('Accès coupé. L’application ne reçoit plus aucune donnée.');
      setConfirmRevoke(null);
      await loadApps();
    } catch (e) {
      setError(messageOf(e));
    } finally {
      setRevoking(null);
    }
  };

  const saveDiscount = async () => {
    if (!editing) return;
    const maxDiscountPct = parseDiscount(editing.value);
    if (maxDiscountPct === null) {
      setError('La remise maximale doit être comprise entre 0 et 90 %.');
      return;
    }
    setSaving(true);
    setError(null);
    try {
      await partnerLinksApi.update(editing.id, { maxDiscountPct });
      setNotice('Remise maximale enregistrée. L’application est prévenue.');
      setEditing(null);
      await loadApps();
    } catch (e) {
      setError(messageOf(e));
    } finally {
      setSaving(false);
    }
  };

  const active = useMemo(() => apps.filter((a) => a.status === 'active'), [apps]);
  const history = useMemo(() => apps.filter((a) => a.status !== 'active'), [apps]);

  const toggleScope = (scope: PartnerScope) =>
    setScopes((current) => (current.includes(scope) ? current.filter((s) => s !== scope) : [...current, scope]));

  return (
    <PageContainer>
      <div className="max-w-3xl mx-auto px-4 py-6 sm:py-10 space-y-6">
        <div>
          <Link to="/company" className="inline-flex items-center gap-1 text-sm text-gray-500 hover:text-[#197ca8]">
            <ArrowLeft className="w-4 h-4" /> Mon entreprise
          </Link>
          <h1 className="mt-3 text-2xl font-bold text-gray-900 flex items-center gap-2">
            <PlugZap className="w-6 h-6 text-[#197ca8]" /> Applications connectées
          </h1>
          <p className="mt-1 text-gray-600">
            Autorisez une application partenaire à vendre vos articles en ligne. Vous choisissez ce qu’elle peut voir
            et vous pouvez couper l’accès à tout moment.
          </p>
        </div>

        {error && (
          <div role="alert" className="flex items-start gap-2 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-800">
            <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" />
            <span>{error}</span>
          </div>
        )}
        {notice && (
          <div role="status" className="flex items-start gap-2 rounded-lg border border-green-200 bg-green-50 p-3 text-sm text-green-800">
            <CheckCircle2 className="w-4 h-4 mt-0.5 flex-shrink-0" />
            <span>{notice}</span>
          </div>
        )}

        {/* Demande à examiner */}
        {!request ? (
          <section className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
            <h2 className="font-semibold text-gray-900 flex items-center gap-2">
              <KeyRound className="w-5 h-5 text-[#197ca8]" /> Autoriser une application
            </h2>
            <p className="mt-1 text-sm text-gray-600">
              Saisissez le code à 8 caractères affiché par l’application, ou ouvrez le lien qu’elle vous a envoyé.
            </p>
            <form
              className="mt-4 flex flex-col sm:flex-row gap-3"
              onSubmit={(e) => {
                e.preventDefault();
                examine(code);
              }}
            >
              <input
                value={code}
                onChange={(e) => setCode(normalizeCode(e.target.value))}
                placeholder="Ex. K7QM4TZP"
                aria-label="Code de connexion"
                autoComplete="off"
                className="flex-1 rounded-lg border border-gray-300 px-3 py-2 font-mono tracking-widest uppercase focus:border-[#197ca8] focus:outline-none focus:ring-2 focus:ring-[#197ca8]/30"
              />
              <button
                type="submit"
                disabled={busy === 'preview' || code.length !== 8}
                className="inline-flex items-center justify-center gap-2 rounded-lg bg-[#197ca8] px-4 py-2 font-medium text-white hover:bg-[#156a92] disabled:opacity-50"
              >
                {busy === 'preview' && <Loader2 className="w-4 h-4 animate-spin" />} Examiner la demande
              </button>
            </form>
          </section>
        ) : (
          <section className="rounded-xl border-2 border-[#197ca8]/40 bg-white p-5 shadow-sm space-y-5">
            <div>
              <p className="text-xs font-semibold uppercase tracking-wide text-[#197ca8]">Demande de connexion</p>
              <h2 className="mt-1 text-lg font-semibold text-gray-900">{request.clientName} souhaite se connecter</h2>
              <p className="mt-1 text-sm text-gray-600">
                Compte chez {request.clientName} : <strong>{request.displayName}</strong>
                {request.partnerSubject && request.partnerSubject !== request.displayName ? ` (${request.partnerSubject})` : ''}.
                Vérifiez que c’est bien vous ou votre équipe qui avez fait cette demande.
              </p>
              <p className="mt-1 text-xs text-gray-500">Demande valable jusqu’au {formatDate(request.expiresAt)}.</p>
            </div>

            <fieldset>
              <legend className="text-sm font-medium text-gray-900">Ce que l’application pourra faire</legend>
              <div className="mt-2 space-y-2">
                {request.requestedScopes.map((scope) => (
                  <label key={scope} className="flex items-start gap-3 rounded-lg border border-gray-200 p-3 cursor-pointer hover:bg-gray-50">
                    <input
                      type="checkbox"
                      className="mt-1 h-4 w-4 accent-[#197ca8]"
                      checked={scopes.includes(scope)}
                      onChange={() => toggleScope(scope)}
                    />
                    <span>
                      <span className="block text-sm font-medium text-gray-900">{SCOPE_LABELS[scope]?.title || scope}</span>
                      <span className="block text-xs text-gray-600">{SCOPE_LABELS[scope]?.detail}</span>
                    </span>
                  </label>
                ))}
              </div>
            </fieldset>

            <div className="grid gap-4 sm:grid-cols-2">
              <label className="block">
                <span className="text-sm font-medium text-gray-900">Point de vente concerné</span>
                <select
                  value={businessUnitId}
                  onChange={(e) => setBusinessUnitId(e.target.value)}
                  className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 focus:border-[#197ca8] focus:outline-none"
                >
                  <option value="">Toute l’entreprise</option>
                  {request.businessUnits.map((u) => (
                    <option key={u.id} value={u.id}>
                      {u.name}
                    </option>
                  ))}
                </select>
              </label>
              <label className="block">
                <span className="text-sm font-medium text-gray-900">Devise d’affichage</span>
                <select
                  value={displayCurrency}
                  onChange={(e) => setDisplayCurrency(e.target.value)}
                  className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 focus:border-[#197ca8] focus:outline-none"
                >
                  {(request.displayCurrencies?.length ? request.displayCurrencies : ['CDF', 'USD']).map((c) => (
                    <option key={c} value={c}>
                      {c === 'CDF' ? 'Franc congolais (CDF)' : c === 'USD' ? 'Dollar américain (USD)' : c}
                    </option>
                  ))}
                </select>
              </label>
            </div>

            <label className="block">
              <span className="text-sm font-medium text-gray-900">Remise maximale autorisée (%)</span>
              <input
                type="number"
                inputMode="decimal"
                min={0}
                max={90}
                step="0.5"
                value={discount}
                onChange={(e) => setDiscount(e.target.value)}
                className="mt-1 w-full sm:w-40 rounded-lg border border-gray-300 px-3 py-2 focus:border-[#197ca8] focus:outline-none"
              />
              <span className="mt-1 block text-xs text-gray-600">
                L’application pourra négocier jusqu’à ce pourcentage sous votre prix de vente. 0 : prix fixe.
              </span>
            </label>

            <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-3">
              <button
                type="button"
                onClick={refuse}
                disabled={busy !== null}
                className="inline-flex items-center justify-center gap-2 rounded-lg border border-gray-300 px-4 py-2 font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
              >
                {busy === 'refuse' ? <Loader2 className="w-4 h-4 animate-spin" /> : <XCircle className="w-4 h-4" />} Refuser
              </button>
              <button
                type="button"
                onClick={approve}
                disabled={busy !== null || !scopes.length}
                className="inline-flex items-center justify-center gap-2 rounded-lg bg-[#197ca8] px-4 py-2 font-medium text-white hover:bg-[#156a92] disabled:opacity-50"
              >
                {busy === 'approve' ? <Loader2 className="w-4 h-4 animate-spin" /> : <ShieldCheck className="w-4 h-4" />} Autoriser
              </button>
            </div>
          </section>
        )}

        {/* Liens actifs */}
        <section>
          <h2 className="font-semibold text-gray-900 flex items-center gap-2">
            <Link2 className="w-5 h-5 text-[#197ca8]" /> Applications autorisées
          </h2>
          {loadingApps ? (
            <div className="mt-3 space-y-3">
              {[0, 1].map((i) => (
                <div key={i} className="h-20 rounded-xl bg-gray-100 animate-pulse" />
              ))}
            </div>
          ) : active.length === 0 ? (
            <p className="mt-3 rounded-xl border border-dashed border-gray-300 p-5 text-sm text-gray-600 text-center">
              Aucune application n’a accès à vos données.
            </p>
          ) : (
            <ul className="mt-3 space-y-3">
              {active.map((app) => (
                <li key={app.id} className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
                  <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
                    <div className="min-w-0">
                      <p className="font-medium text-gray-900 flex items-center gap-2">
                        <Store className="w-4 h-4 text-[#197ca8]" /> {app.clientName}
                        <span className="text-gray-500 font-normal truncate">{app.displayName}</span>
                      </p>
                      <p className="mt-1 text-xs text-gray-500">
                        {app.businessUnitName || 'Toute l’entreprise'} · Devise {app.displayCurrency || 'CDF'} · Autorisée le{' '}
                        {formatDate(app.approvedAt)}
                      </p>
                      {editing?.id === app.id ? (
                        <div className="mt-2 flex flex-wrap items-center gap-2">
                          <label className="text-xs text-gray-700" htmlFor={`discount-${app.id}`}>
                            Remise maximale (%)
                          </label>
                          <input
                            id={`discount-${app.id}`}
                            type="number"
                            inputMode="decimal"
                            min={0}
                            max={90}
                            step="0.5"
                            value={editing.value}
                            onChange={(e) => setEditing({ id: app.id, value: e.target.value })}
                            className="w-24 rounded-lg border border-gray-300 px-2 py-1 text-sm focus:border-[#197ca8] focus:outline-none"
                          />
                          <button
                            type="button"
                            onClick={saveDiscount}
                            disabled={saving}
                            className="inline-flex items-center gap-1 rounded-lg bg-[#197ca8] px-3 py-1 text-sm font-medium text-white hover:bg-[#156a92] disabled:opacity-50"
                          >
                            {saving && <Loader2 className="w-4 h-4 animate-spin" />} Enregistrer
                          </button>
                          <button type="button" onClick={() => setEditing(null)} className="rounded-lg px-2 py-1 text-sm text-gray-600 hover:bg-gray-100">
                            Annuler
                          </button>
                        </div>
                      ) : (
                        <p className="mt-1 text-xs text-gray-600">
                          Remise maximale : <strong>{Number(app.maxDiscountPct || 0)} %</strong>{' '}
                          <button
                            type="button"
                            onClick={() => setEditing({ id: app.id, value: String(app.maxDiscountPct ?? 0) })}
                            className="ml-1 text-[#197ca8] hover:underline"
                          >
                            Modifier
                          </button>
                        </p>
                      )}
                      <div className="mt-2 flex flex-wrap gap-1.5">
                        {app.scopes.map((s) => (
                          <span key={s} className="rounded-full bg-[#197ca8]/10 px-2 py-0.5 text-xs font-medium text-[#197ca8]">
                            {SCOPE_LABELS[s]?.title || s}
                          </span>
                        ))}
                      </div>
                    </div>
                    {confirmRevoke === app.id ? (
                      <div className="flex gap-2 sm:flex-col sm:items-end">
                        <button
                          type="button"
                          onClick={() => revoke(app.id)}
                          disabled={revoking === app.id}
                          className="inline-flex items-center gap-1 rounded-lg bg-red-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-red-700 disabled:opacity-50"
                        >
                          {revoking === app.id && <Loader2 className="w-4 h-4 animate-spin" />} Confirmer
                        </button>
                        <button
                          type="button"
                          onClick={() => setConfirmRevoke(null)}
                          className="rounded-lg px-3 py-1.5 text-sm text-gray-600 hover:bg-gray-100"
                        >
                          Annuler
                        </button>
                      </div>
                    ) : (
                      <button
                        type="button"
                        onClick={() => setConfirmRevoke(app.id)}
                        className="self-start rounded-lg border border-red-200 px-3 py-1.5 text-sm font-medium text-red-700 hover:bg-red-50"
                      >
                        Couper l’accès
                      </button>
                    )}
                  </div>
                </li>
              ))}
            </ul>
          )}
        </section>

        {history.length > 0 && (
          <details className="rounded-xl border border-gray-200 bg-white p-4">
            <summary className="cursor-pointer text-sm font-medium text-gray-700">Accès coupés ({history.length})</summary>
            <ul className="mt-3 divide-y divide-gray-100">
              {history.map((app) => (
                <li key={app.id} className="py-2 text-sm text-gray-600">
                  <span className="font-medium text-gray-800">{app.clientName}</span> {app.displayName}
                  {app.revokedAt ? ` · coupé le ${formatDate(app.revokedAt)}` : ''}
                </li>
              ))}
            </ul>
          </details>
        )}
      </div>
    </PageContainer>
  );
}
